import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useContent } from '../context/ContentContext';
import { Users, ShieldCheck, Zap } from 'lucide-react';

const About: React.FC = () => {
  const { content } = useContent();
  const cultureItems = content.about.culture || [];
  const sectionRef = useRef<HTMLElement>(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="about" ref={sectionRef} className="py-24 bg-white overflow-hidden relative">
      {/* 视差背景图 - 提高透明度确保可见性 */}
      <motion.div 
        style={{ y: backgroundY }}
        className="absolute inset-0 z-0 pointer-events-none"
      >
        <img 
          src="https://images.unsplash.com/photo-1497215842964-222b4bef97ed?auto=format&fit=crop&q=80&w=1920" 
          alt="Office Background" 
          className="w-full h-[120%] object-cover opacity-[0.08] grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white/40 to-white"></div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">公司简介</h2>
          <p className="text-[#D4AF37] tracking-widest font-semibold text-sm">COMPANY PROFILE</p>
          <div className="w-16 h-1 bg-[#D4AF37] mx-auto mt-4 rounded-full"></div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative group px-4 sm:px-0"
          >
             {/* 装饰性外框 - 调淡色调以匹配明亮主图 */}
             <div className="absolute top-0 left-0 w-16 h-16 sm:w-24 sm:h-24 border-t-2 border-l-2 border-[#D4AF37]/30 -translate-x-2 -translate-y-2 sm:-translate-x-4 sm:-translate-y-4 transition-all duration-500 group-hover:-translate-x-2 group-hover:-translate-y-2 group-hover:w-32 group-hover:h-32 group-hover:border-[#D4AF37]"></div>
             
             <div className="relative overflow-hidden rounded-lg shadow-[0_30px_60px_rgba(0,0,0,0.06)] bg-slate-50">
                {/* 替换为采光极佳的明亮办公环境图 */}
                <img 
                  src={content.about.image || "https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=1200"} 
                  alt="Bright Modern Corporate Office" 
                  loading="lazy"
                  className="w-full h-auto object-cover transform transition-all duration-[2s] group-hover:scale-110"
                />
                {/* 轻微的渐变增强质感，不再使用深色遮罩 */}
                <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
             </div>
             
             <div className="absolute bottom-0 right-0 w-16 h-16 sm:w-24 sm:h-24 border-b-2 border-r-2 border-[#D4AF37]/30 translate-x-2 translate-y-2 sm:translate-x-4 sm:translate-y-4 transition-all duration-500 group-hover:translate-x-2 group-hover:translate-y-2 group-hover:w-32 group-hover:h-32 group-hover:border-[#D4AF37]"></div>
             
             {/* 悬浮亮点卡片 - 增强阴影以在明亮背景上突出 */}
             <div className="absolute -bottom-6 -right-2 sm:-bottom-8 sm:-right-8 bg-white/95 backdrop-blur-xl text-slate-900 p-4 sm:p-6 shadow-[0_20px_50px_rgba(0,0,0,0.12)] border-l-4 border-[#D4AF37] block z-10 max-w-[200px] sm:max-w-xs transform transition-transform duration-500 group-hover:translate-y-[-10px]">
                <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                  <div className="bg-[#D4AF37]/10 p-1.5 sm:p-2 rounded-lg shrink-0">
                    <Users className="text-[#D4AF37]" size={18} />
                  </div>
                  <p className="text-sm sm:text-xl font-bold tracking-tight text-slate-900">“1+N” 服务模式</p>
                </div>
                <p className="text-slate-500 text-[10px] sm:text-xs leading-relaxed font-medium">1个专属财税管家 + N个专家顾问团，提供无缝对接、多维深度的专业支持。</p>
             </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:pl-8 mt-12 lg:mt-0"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-8 relative inline-block">
              {content.hero.companyNameCn}
              <span className="absolute bottom-1 left-0 w-full h-3 bg-[#D4AF37]/10 -z-10"></span>
            </h3>
            <div className="space-y-6 text-gray-600 leading-relaxed text-justify">
              {content.about.intro.map((paragraph, idx) => (
                <p key={idx} className={`${idx === 0 ? 'font-medium text-slate-800 text-lg mb-2' : 'text-base'}`}>
                  {paragraph}
                </p>
              ))}
            </div>
            
            <div className="mt-10 grid grid-cols-2 gap-4">
              <div className="p-5 bg-slate-50/50 rounded-xl border border-slate-100 hover:bg-white hover:shadow-lg hover:border-[#D4AF37]/20 transition-all duration-300">
                <ShieldCheck className="text-[#D4AF37] mb-3" size={28} />
                <h4 className="font-bold text-slate-800 text-base mb-1">合规为基</h4>
                <p className="text-xs text-gray-500">严守行业规范，筑牢企业发展安全底线</p>
              </div>
              <div className="p-5 bg-slate-50/50 rounded-xl border border-slate-100 hover:bg-white hover:shadow-lg hover:border-[#D4AF37]/20 transition-all duration-300">
                <Zap className="text-[#D4AF37] mb-3" size={28} />
                <h4 className="font-bold text-slate-800 text-base mb-1">价值为核</h4>
                <p className="text-xs text-gray-500">洞察政策红利，实现财务管理策略增值</p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="bg-slate-50/50 rounded-3xl p-6 md:p-16 border border-slate-100 relative overflow-hidden"
        >
          <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>

          <div className="text-center mb-12 sm:mb-16 relative z-10">
            <h3 className="text-xl sm:text-2xl font-bold text-slate-900">企业核心价值</h3>
            <p className="text-[#D4AF37] uppercase tracking-[0.3em] text-[10px] font-black mt-2">Core Values & Philosophy</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 relative z-10">
            {cultureItems.map((item, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 group hover:-translate-y-2 border border-transparent hover:border-[#D4AF37]/20"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-slate-50 rounded-2xl flex items-center justify-center mb-6 text-[#D4AF37] font-bold text-xl group-hover:bg-[#D4AF37] group-hover:text-white transition-all duration-500 shadow-inner group-hover:rotate-6">
                  {index + 1}
                </div>
                
                <h4 className="text-lg sm:text-xl font-bold text-slate-800 mb-2">{item.title}</h4>
                <p className="text-[10px] text-gray-400 font-black mb-4 tracking-widest uppercase border-b border-gray-100 pb-2">{item.enTitle}</p>
                <p className="text-sm text-gray-600 leading-relaxed group-hover:text-slate-900 transition-colors">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;